import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import anaPanelImage from '@/pages/images/ana-panel.png';
import defterKasaLogo from '@/pages/images/defter-kasa-logo.png';
import { useProductProgress } from '@/context/ProductProgressContext';
/* Defter Kasa ilerleme barı */
function ProgressBar() {
  const { t } = useTranslation();
  const progressValue = useProductProgress('defter-kasa');
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let frame: number;
    const start = performance.now();
    const duration = 1400;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * progressValue));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, progressValue]);

  return (
    <div ref={ref} className="mt-10 mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('dk.progress.label')}</span>
        <span className="font-mono text-2xl font-black text-white tabular-nums">{displayed}<span className="text-neutral-600 text-base">%</span></span>
      </div>
      <div className="relative h-1.5 bg-white/5 w-full overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-white transition-none"
          style={{ width: started ? `${progressValue}%` : '0%', transition: started ? 'width 1.4s cubic-bezier(0.16,1,0.3,1)' : 'none' }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono text-[10px] text-neutral-700 tracking-widest">{t('dk.progress.stable')}</span>
        <span className="font-mono text-[10px] text-neutral-700 tracking-widest">{t('dk.progress.status')}</span>
      </div>
    </div>
  );
}

export default function HeroBlock() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen bg-[#070b12] flex flex-col overflow-hidden pt-16">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 16% 18%, rgba(29,78,216,0.18) 0%, rgba(29,78,216,0) 45%), radial-gradient(circle at 82% 20%, rgba(56,189,248,0.14) 0%, rgba(56,189,248,0) 42%), radial-gradient(circle at 60% 82%, rgba(30,64,175,0.16) 0%, rgba(30,64,175,0) 48%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.24]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.09) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="absolute top-20 left-8 md:left-16 w-[70%] md:w-[42%] h-28 border border-sky-300/10 bg-slate-900/30 backdrop-blur-[2px] rounded-md pointer-events-none" />
      <div className="absolute top-36 right-6 md:right-20 w-[62%] md:w-[34%] h-24 border border-blue-300/10 bg-slate-900/25 rounded-md pointer-events-none" />
      <div className="absolute bottom-24 left-10 md:left-24 w-[55%] md:w-[30%] h-20 border border-indigo-300/10 bg-slate-900/20 rounded-md pointer-events-none" />

      <svg
        className="absolute top-[18%] left-[5%] w-[46%] h-[180px] opacity-30 hidden md:block pointer-events-none"
        viewBox="0 0 560 180"
        fill="none"
        aria-hidden="true"
      >
        <polyline
          points="8,140 64,128 128,134 194,92 258,104 322,68 388,74 454,44 520,56 552,40"
          stroke="url(#dk-finance-line-a)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="454" cy="44" r="4.5" fill="#93c5fd" />
        <defs>
          <linearGradient id="dk-finance-line-a" x1="8" y1="160" x2="552" y2="20" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgba(147,197,253,0.15)" />
            <stop offset="65%" stopColor="rgba(125,211,252,0.7)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.85)" />
          </linearGradient>
        </defs>
      </svg>

      <svg
        className="absolute bottom-[12%] right-[4%] w-[38%] h-[140px] opacity-25 hidden lg:block pointer-events-none"
        viewBox="0 0 420 140"
        fill="none"
        aria-hidden="true"
      >
        <polyline
          points="10,118 54,106 98,110 142,92 186,96 230,78 274,82 318,66 362,70 408,52"
          stroke="url(#dk-finance-line-b)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <defs>
          <linearGradient id="dk-finance-line-b" x1="10" y1="124" x2="408" y2="42" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgba(125,211,252,0.12)" />
            <stop offset="75%" stopColor="rgba(147,197,253,0.6)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.65)" />
          </linearGradient>
        </defs>
      </svg>

      <div className="absolute top-24 right-0 w-px h-64 bg-gradient-to-b from-transparent via-sky-200/20 to-transparent" />
      <div className="absolute top-24 right-0 w-72 h-px bg-gradient-to-r from-transparent via-sky-200/20 to-transparent" />

      <div className="flex-1 flex flex-col justify-center px-6 lg:px-14 py-20 lg:py-28">
        <RevealWrapper direction="up" duration={500}>
          <div className="flex items-center gap-3 mb-10">
            <button
              onClick={() => navigate('/')}
              className="font-mono text-xs text-neutral-600 hover:text-neutral-400 tracking-widest uppercase transition-colors duration-200 flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              {t('product.back')}
            </button>
            <span className="text-neutral-800">/</span>
            <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('product.breadcrumb.products')}</span>
            <span className="text-neutral-800">/</span>
            <span className="font-mono text-xs text-white tracking-widest uppercase">{t('dk.name')}</span>
          </div>
        </RevealWrapper>

        <div className="grid lg:grid-cols-2 gap-16 xl:gap-28 items-center">
          <RevealWrapper direction="left" duration={700}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-xs px-2.5 py-1 border border-white/30 text-white bg-white/5 tracking-widest">{t('dk.status')}</span>
                <span className="font-mono text-xs text-neutral-600 tracking-widest">{t('dk.meta')}</span>
              </div>

              <div className="flex items-center gap-5 mb-8">
                <div className="w-20 h-20 border border-sky-300/20 flex items-center justify-center bg-gradient-to-br from-[#0e1d38] via-[#102544] to-[#0a1426] shrink-0">
                  <img src={defterKasaLogo} alt={t('dk.name')} className="w-16 h-16 object-contain" />
                </div>
                <h1 className="font-black text-white text-5xl lg:text-6xl tracking-tight leading-none">
                  {t('dk.name.line1')}<br />{t('dk.name.line2')}
                </h1>
              </div>

              <p className="text-neutral-400 text-base leading-loose max-w-md mb-12">
                {t('dk.description')}
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="https://play.google.com/store/apps/details?id=com.companyname.kucukesnafcari&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-mono text-xs font-bold tracking-widest uppercase overflow-hidden transition-all duration-300 hover:scale-105 sm:whitespace-nowrap"
                >
                  <span className="relative z-10 group-hover:text-white transition-colors duration-300 flex items-center gap-2">
                    <i className="ri-google-play-line text-base" />
                    {t('dk.cta.play')}
                  </span>
                  <div className="absolute inset-0 bg-black translate-x-[-101%] group-hover:translate-x-0 transition-transform duration-300" />
                </a>
                <button
                  onClick={() => navigate('/defter-kasa/gizlilik-politikasi')}
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:border-white/60 transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-shield-2-line" />
                  {t('dk.cta.privacy')}
                </button>
              </div>

              <ProgressBar />
            </div>
          </RevealWrapper>

          <RevealWrapper direction="right" delay={200} duration={700}>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-white/5 blur-3xl rounded-full scale-75" />
                <div className="relative bg-[#1a1a1a] border border-white/15 rounded-[36px] overflow-hidden shadow-2xl" style={{ width: 260, height: 520 }}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1a1a1a] rounded-b-2xl z-10 flex items-center justify-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
                    <div className="w-8 h-1 rounded-full bg-neutral-800" />
                  </div>
                  <div className="w-full h-full relative bg-[#0d1117]">
                    <img
                      src={anaPanelImage}
                      alt={t('dk.name')}
                      className="w-full h-full object-cover object-bottom"
                    />
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/5 via-transparent to-black/10" />
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
        </div>
      </div>

      <div className="border-t border-white/5 px-6 lg:px-14 py-4 flex items-center justify-between">
        <span className="font-mono text-xs text-neutral-700">{t('dk.footer.left')}</span>
        <span className="font-mono text-xs text-neutral-700">{t('dk.footer.right')}</span>
      </div>
    </section>
  );
}

